import { useCallback, useEffect, useState } from 'react';
import { debugConfigService } from '../services/debugConfig';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export function useRecaptcha() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(true); // Default to enabled

  // Check if reCAPTCHA is enabled in debug config
  useEffect(() => {
    const checkEnabled = async () => {
      try {
        const enabled = await debugConfigService.isRecaptchaEnabled();
        setIsEnabled(enabled);
      } catch (err) {
        // Default to enabled if we can't check
        setIsEnabled(true);
      }
    };
    checkEnabled();
  }, []);

  useEffect(() => {
    // Don't load script if reCAPTCHA is disabled
    if (!isEnabled) {
      console.log('[reCAPTCHA] Disabled via admin settings');
      return;
    }

    // Check if already loaded
    if (window.grecaptcha) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setIsLoaded(true));
      return;
    }

    // Skip loading if no site key configured
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY_HERE') {
      console.warn('[reCAPTCHA] Site key not configured. Skipping reCAPTCHA loading.');
      setError('reCAPTCHA not configured');
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => {
        setIsLoaded(true);
      });
    };

    script.onerror = () => {
      setError('Failed to load reCAPTCHA');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup not needed for reCAPTCHA script
    };
  }, [isEnabled]);

  const executeRecaptcha = useCallback(async (action: string): Promise<string | null> => {
    // If disabled via admin settings, skip token generation
    if (!isEnabled) {
      console.log('[reCAPTCHA] Skipping token generation - disabled via admin settings');
      return null;
    }

    // If not configured, return null (allows forms to work without reCAPTCHA in dev)
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY_HERE') {
      console.warn('[reCAPTCHA] Skipping token generation - not configured');
      return null;
    }

    if (!isLoaded || !window.grecaptcha) {
      console.error('[reCAPTCHA] Not loaded yet');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      return token;
    } catch (err) {
      console.error('[reCAPTCHA] Failed to execute:', err);
      return null;
    }
  }, [isLoaded, isEnabled]);

  return {
    isLoaded,
    isEnabled,
    error,
    executeRecaptcha,
    isConfigured: RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY_HERE'
  };
}
